import { z } from 'zod';
/**
 * Contrato de autenticação compartilhado entre web e api.
 * O backend valida com estes schemas; o frontend infere os tipos deles.
 * Nada aqui pode importar de apps/ — este pacote é a única ponte entre os dois.
 */
export const emailSchema = z.string().trim().toLowerCase().email('E-mail inválido');
export const passwordSchema = z
    .string()
    .min(8, 'A senha deve ter no mínimo 8 caracteres')
    .max(128, 'A senha deve ter no máximo 128 caracteres');
export const loginSchema = z.object({
    email: emailSchema,
    password: z.string().min(1, 'Informe a senha'),
});
export const refreshSchema = z.object({
    refreshToken: z.string().min(1),
});
/** Perfis por área, espelhando o organograma da seção 5 do PRD. */
export const UserRole = {
    ADMIN: 'ADMIN',
    PCM: 'PCM',
    CCO: 'CCO',
    PLANTAO: 'PLANTAO',
    SOCORRO: 'SOCORRO',
    MANUTENCAO: 'MANUTENCAO',
    MECANICO: 'MECANICO',
    INSPETOR: 'INSPETOR',
    ESTOQUE: 'ESTOQUE',
    LIMPEZA: 'LIMPEZA',
    GESTAO: 'GESTAO',
};
export const userRoleSchema = z.nativeEnum(UserRole);
export const USER_ROLE_LABELS = {
    ADMIN: 'Administrador',
    PCM: 'PCM',
    CCO: 'CCO',
    PLANTAO: 'Plantão',
    SOCORRO: 'Socorro',
    MANUTENCAO: 'Manutenção',
    MECANICO: 'Mecânico',
    INSPETOR: 'Inspetor',
    ESTOQUE: 'Estoque',
    LIMPEZA: 'Limpeza',
    GESTAO: 'Gestão',
};
/** Onde os avisos rápidos (toasts) aparecem na tela. */
export const TOAST_POSITIONS = ['top-left', 'top-right', 'bottom-left', 'bottom-right'];
/** Máximo de atalhos no cabeçalho. */
export const MAX_SHORTCUTS = 4;
/**
 * Preferências da própria pessoa — guardadas na conta, não no navegador, para
 * seguirem o usuário de uma máquina a outra.
 */
export const userPreferencesSchema = z.object({
    /** Chaves de navegação (as mesmas do menu lateral), na ordem do cabeçalho. */
    shortcuts: z.array(z.string().min(1).max(40)).max(MAX_SHORTCUTS).default([]),
    notifications: z.boolean().default(true),
    toastPosition: z.enum(TOAST_POSITIONS).default('bottom-right'),
});
export const updatePreferencesSchema = userPreferencesSchema.partial();
export const publicUserSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    email: z.string().email(),
    role: userRoleSchema,
    registration: z.string().nullable(),
    garageId: z.string().uuid().nullable(),
    phone: z.string().nullable(),
    avatarUrl: z.string().nullable(),
    preferences: userPreferencesSchema,
    isActive: z.boolean(),
    /** Senha provisória: a pessoa precisa definir a sua antes de usar o sistema. */
    mustChangePassword: z.boolean(),
    createdAt: z.string().datetime(),
});
// --- Resumo e atividades da própria conta ----------------------------------
export const ACCOUNT_ACTIVITY_CATEGORIES = ['security', 'profile', 'preferences', 'system'];
export const accountActivityQuerySchema = z.object({
    category: z.enum(ACCOUNT_ACTIVITY_CATEGORIES).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(60),
});
export const authTokensSchema = z.object({
    accessToken: z.string(),
    refreshToken: z.string(),
    expiresIn: z.number().int().positive(),
});
export const authResponseSchema = z.object({
    user: publicUserSchema,
    tokens: authTokensSchema,
});
// --- Recuperação de senha ----------------------------------------------------
export const forgotPasswordSchema = z.object({
    email: emailSchema,
});
export const resetPasswordSchema = z.object({
    token: z.string().min(20, 'Link inválido').max(200),
    newPassword: passwordSchema,
});
// --- Administração de usuários (RF-36) -------------------------------------
export const createUserSchema = z.object({
    name: z.string().trim().min(2, 'Informe o nome').max(120),
    email: emailSchema,
    password: passwordSchema,
    role: userRoleSchema,
    registration: z.string().trim().max(32).optional(),
    garageId: z.string().uuid().optional(),
    /** Por padrão a senha inicial é provisória: a pessoa define a sua no primeiro acesso. */
    mustChangePassword: z.boolean().default(true),
});
export const updateUserSchema = z.object({
    name: z.string().trim().min(2).max(120).optional(),
    password: passwordSchema.optional(),
    /** Com nova senha, vale `true` se não vier nada: senha dada pelo administrador é provisória. */
    mustChangePassword: z.boolean().optional(),
    role: userRoleSchema.optional(),
    registration: z.string().trim().max(32).optional(),
    garageId: z.string().uuid().optional(),
    isActive: z.boolean().optional(),
});
export const userQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    perPage: z.coerce.number().int().min(1).max(100).default(20),
    search: z.string().trim().max(60).optional(),
    role: userRoleSchema.optional(),
    onlyActive: z.coerce.boolean().default(true),
});
// --- Perfil da própria pessoa ---------------------------------------------
export const updateProfileSchema = z.object({
    name: z.string().trim().min(2, 'Informe o nome').max(120),
    phone: z.string().trim().max(32).optional(),
    /** Data URL de imagem (JPEG/PNG/WebP), no máximo ~300 KB — redimensionada no navegador. */
    avatarUrl: z
        .string()
        .max(400_000, 'Imagem grande demais')
        .regex(/^data:image\/(png|jpeg|webp);base64,/, 'Imagem inválida')
        .nullable()
        .optional(),
});
/** Código de erro da API quando a conta ainda está com senha provisória. */
export const PASSWORD_CHANGE_REQUIRED = 'PASSWORD_CHANGE_REQUIRED';
export const changePasswordSchema = z.object({
    currentPassword: z.string().min(1, 'Informe a senha atual'),
    newPassword: passwordSchema,
});
//# sourceMappingURL=auth.schemas.js.map